import type { PageRouteDef, RouteSegment, RouteTemplate, UnknownQueryParam } from "@octopusdeploy/portal-routes";
import type { PageRegistration } from "~/routing/pageRegistrations/PageRegistration";
export function createPageMap<RouteParams, ChildRouteSegments extends RouteSegmentsConstraint, ChildPages extends PagesConstraint, PageRegistrationDefinitions extends PageGroupRegistrationDefinition<RouteParams, ChildRouteSegments, ChildPages>>(routeSegment: RouteSegment<RouteParams, ChildRouteSegments, ChildPages>, pageRegistrations: PageRegistrationDefinitions): PageGroupRegistration<PageRegistrationDefinitions> {
    const childGroups = Object.entries(routeSegment.childRouteSegments).reduce<PageGroupsRegistration<PageRegistrationDefinitions["childGroups"]>>((p, [key, childRouteSegment]) => ({
        ...p,
        [key]: createPageMap(childRouteSegment, pageRegistrations.childGroups[key]),
    }), 
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    {} as PageGroupsRegistration<PageRegistrationDefinitions["childGroups"]>);
    const pages = Object.entries(routeSegment.pages).reduce<Pages<PageRegistrationDefinitions["pages"]>>((p, [key, pageRoute]) => ({
        ...p,
        [key]: pageRegistrations.pages[key](pageRoute),
    }), 
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    {} as Pages<PageRegistrationDefinitions["pages"]>);
    return {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        partialRoute: pageRegistrations.partialRoute(routeSegment.partialRoute) as ExtractPartialRoute<PageRegistrationDefinitions["partialRoute"]>,
        childGroups,
        pages,
    };
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PageGroupsRegistration<PageGroups extends Record<string, PageGroupRegistrationDefinition<any, any, any>>> = {
    [K in keyof PageGroups]: PageGroupRegistration<PageGroups[K]>;
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type PageGroupRegistration<RegistrationDefinition extends PageGroupRegistrationDefinition<any, any, any>> = {
    partialRoute: ExtractPartialRoute<RegistrationDefinition["partialRoute"]>;
    childGroups: PageGroupsRegistration<RegistrationDefinition["childGroups"]>;
    pages: Pages<RegistrationDefinition["pages"]>;
};
type Pages<Pages> = {
    [K in keyof Pages]: Page<Pages[K]>;
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Page<SinglePageRegistration> = SinglePageRegistration extends PageRegistrationDefinition<any, any, infer ConcreteRegistration> ? ConcreteRegistration : never;
export interface PageGroupRegistrationDefinition<RouteParams, RouteSegments, Pages> {
    partialRoute: PartialRouteGetter<RouteParams>;
    pages: ChildPages<Pages>;
    childGroups: PageGroupsRegistrationDefinititons<RouteSegments>;
}
type PageGroupsRegistrationDefinititons<RouteSegments> = {
    [K in keyof RouteSegments]: RouteSegments[K] extends RouteSegment<infer ChildRouteParams, infer ChildRouteSegments, infer ChildPages> ? PageGroupRegistrationDefinition<ChildRouteParams, ChildRouteSegments, ChildPages> : never;
};
type ChildPages<Pages> = {
    [K in keyof Pages]: Pages[K] extends PageRouteDef<infer RouteParams, infer QueryParams> ? PageRegistrationDefinition<RouteParams, QueryParams, any> : never;
};
type ExtractPartialRoute<PartialRouteGetterType> = PartialRouteGetterType extends PartialRouteGetter<infer RouteParams> ? RouteTemplate<RouteParams> : never;
type PartialRouteGetter<RouteParams> = (partialRoute: RouteTemplate<RouteParams>) => RouteTemplate<RouteParams>;
type PageRegistrationDefinition<RouteParams, QueryParams extends UnknownQueryParam[], ConcretePageRegistration extends PageRegistration<RouteParams, QueryParams>> = (route: PageRouteDef<RouteParams, QueryParams>) => ConcretePageRegistration;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PageConstraint = PageRouteDef<any, any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RouteSegmentConstraint = RouteSegment<any, any, any>;
export type RouteSegmentsConstraint = Record<string, RouteSegmentConstraint>;
export type PagesConstraint = Record<string, PageConstraint>;
